import { ChartData, LegendItem } from "./types";
import {
	PieChartWithLabels,
	PieChartWithLabelsRow,
} from "../../../../../dashboard/widgets/charts/PieChartWithLabels";
import { ColorUtils } from "../../../../../components/Utils/color/ColorUtils";
import { CHART_COLORS } from "../../../../../dashboard/widgets/DashboardWidget";
import {
	JSONCensusSuggestedActionsAnalysisSummary,
	Summary,
} from "../../data/models/JSONCensusSuggestedActionsAnalysisSummary";
import {
	CensusEnums,
	CensusEquipmentStatusDetails,
	CensusEquipmentStatusDetailsEnum,
	CensusEquipmentStatuses,
	CensusEquipmentStatusesEnum,
	CensusEquipmentStatusReasons,
} from "../../data/models/CensusEnums";
import { AppTranslations } from "../../../../../components/Utils/translations/AppTranslations";

declare var AppTranslations: AppTranslations;

const FAIL_COLOR = "#7e0000";
const OK_COLOR = "#035600";

export const getPieChartLegend = (list: ChartData): LegendItem[] => {
	return list.items.map((item) => {
		const percentage = (item.frequency * 100) / list.total;
		return {
			color: item.color,
			name: item.label,
			value: item.frequency,
			percentage,
		};
	});
};

const getStatusCount = (list: Summary[], status) => {
	return list?.filter(
		(item) => item?.statusAndDetailCount?.inventoryStatus === status,
	).length;
};

export const createPieCharData = (
	list: JSONCensusSuggestedActionsAnalysisSummary,
	censusEquipmentStatuses: CensusEquipmentStatuses,
): PieChartWithLabelsRow[] => {
	const Ok = {
		label: censusEquipmentStatuses[CensusEquipmentStatusesEnum.OK],
		frequency: 0,
		color: OK_COLOR,
		code: CensusEquipmentStatusesEnum.OK,
		order: 0,
	};
	const Fail = {
		label: censusEquipmentStatuses[CensusEquipmentStatusesEnum.FAIL],
		frequency: 0,
		color: FAIL_COLOR,
		code: CensusEquipmentStatusesEnum.FAIL,
		order: 1,
	};

	list?.summary?.sort(sortByStatusAndCount).forEach((item, index) => {
		if (
			item.statusAndDetailCount?.inventoryStatus ===
			CensusEquipmentStatusesEnum.OK
		) {
			Ok.frequency += item.statusAndDetailCount.count;
		} else if (
			item.statusAndDetailCount?.inventoryStatus ===
			CensusEquipmentStatusesEnum.FAIL
		) {
			Fail.frequency += item.statusAndDetailCount.count;
		}
	});
	return [Ok, Fail];
};

const sortByStatusAndCount = (item1, item2) => {
	if (
		item1.statusAndDetailCount.inventoryStatus >
		item2.statusAndDetailCount.inventoryStatus
	) {
		return 1;
	}
	if (
		item1.statusAndDetailCount.inventoryStatus <
		item2.statusAndDetailCount.inventoryStatus
	) {
		return -1;
	}
	return item1.statusAndDetailCount.count > item2.statusAndDetailCount.count
		? -1
		: 1;
};

export const createOkPieCharData = (
	list: JSONCensusSuggestedActionsAnalysisSummary,
	maxNumberItems: number,
	censusEnums: CensusEnums,
): { data: PieChartWithLabelsRow[]; otherData: PieChartWithLabelsRow[] } => {
	return createCharData(
		list,
		OK_COLOR,
		censusEnums,
		maxNumberItems,
		CensusEquipmentStatusesEnum.OK,
	);
};

export const createCharData = (
	list: JSONCensusSuggestedActionsAnalysisSummary,
	color: string,
	censusEnums: CensusEnums,
	maxNumberItems?: number,
	status?: CensusEquipmentStatusesEnum,
): { data: PieChartWithLabelsRow[]; otherData: PieChartWithLabelsRow[] } => {
	const mappedData = list?.summary
		?.sort(sortByStatusAndCount)
		?.filter((item) => item?.statusAndDetailCount?.inventoryStatus === status)
		.map((item, index) => {
			return {
				label: createChartLabel(item, censusEnums),
				frequency: item.statusAndDetailCount.count,
				color: getColorShade(
					color,
					index,
					getStatusCount(
						list?.summary,
						item.statusAndDetailCount?.inventoryStatus,
					),
				),
				order: index,
				code: `${item.statusAndDetailCount?.inventoryStatus}-${item.statusAndDetailCount?.inventoryStatusDetail}`,
			};
		});
	return {
		data: PieChartWithLabels.prepareData(
			mappedData,
			maxNumberItems,
			"custom",
			{ others: AppTranslations.genericWords.other },
			undefined,
		),
		otherData:
			mappedData.length > maxNumberItems
				? mappedData.slice(maxNumberItems - 1)
				: [],
	};
};

export const createFailPieCharData = (
	list: JSONCensusSuggestedActionsAnalysisSummary,
	maxNumberItems: number,
	censusEnums: CensusEnums,
): { data: PieChartWithLabelsRow[]; otherData: PieChartWithLabelsRow[] } => {
	return createCharData(
		list,
		FAIL_COLOR,
		censusEnums,
		maxNumberItems,
		CensusEquipmentStatusesEnum.FAIL,
	);
};

export const getColorShade = (
	baseColor: string,
	shadeIndex: number,
	numberOfShades: number,
): string => {
	const colorUtils = new ColorUtils(CHART_COLORS);
	return colorUtils.getColorShade(baseColor, shadeIndex, numberOfShades);
};

export const translateCensusStatus = (
	status: CensusEquipmentStatusesEnum,
	censusEquipmentStatuses: CensusEquipmentStatuses,
): string => {
	switch (status) {
		case CensusEquipmentStatusesEnum.OK:
		case CensusEquipmentStatusesEnum.FAIL:
			return censusEquipmentStatuses[status];
		default:
			return status;
	}
};

export const translateCensusStatusDetail = (
	statusDetail: CensusEquipmentStatusDetailsEnum,
	censusEquipmentStatusDetails: CensusEquipmentStatusDetails,
): string => {
	switch (statusDetail) {
		case CensusEquipmentStatusDetailsEnum.NO_TELEMETRY_DATA:
		case CensusEquipmentStatusDetailsEnum.WRITTEN_OFF:
		case CensusEquipmentStatusDetailsEnum.UL:
		case CensusEquipmentStatusDetailsEnum.IN_WAREHOUSE:
		case CensusEquipmentStatusDetailsEnum.NO_NETWORK:
		case CensusEquipmentStatusDetailsEnum.WITH_WARNING:
		case CensusEquipmentStatusDetailsEnum.MISSING:
		case CensusEquipmentStatusDetailsEnum.TO_BE_VISITED:
		case CensusEquipmentStatusDetailsEnum.SEASONAL:
			return censusEquipmentStatusDetails[statusDetail];
		default:
			return statusDetail;
	}
};

export const translateCensusStatusReason = (
	statusReason: CensusEquipmentStatusReasons,
	censusEquipmentStatusReasons: CensusEquipmentStatusReasons,
): string => {
	return censusEquipmentStatusReasons[statusReason];
};

export const createChartLabel = (item: Summary, censusEnums: CensusEnums) => {
	const status = translateCensusStatus(
		item.statusAndDetailCount?.inventoryStatus,
		censusEnums.censusEquipmentStatuses,
	);
	const statusDetails = `${
		item.statusAndDetailCount?.inventoryStatusDetail
			? `: ${translateCensusStatusDetail(
					item.statusAndDetailCount?.inventoryStatusDetail,
					censusEnums?.censusEquipmentStatusDetails,
				)}`
			: ""
	}`;
	const statusReasons = `${
		item.statusAndDetailCount?.inventoryStatusReason
			? `- ${translateCensusStatusReason(
					item.statusAndDetailCount?.inventoryStatusReason,
					censusEnums?.censusEquipmentStatusReasons,
				)}`
			: ""
	}`;

	return `${status}${statusDetails} ${statusReasons}`;
};

export const createData = (dataToMap: {
	data: PieChartWithLabelsRow[];
	otherData: PieChartWithLabelsRow[];
}) => {
	const total = [...dataToMap.data, ...dataToMap.otherData].reduce(
		(acc, item) => acc + item.frequency,
		0,
	);
	const data = getPieChartLegend({
		items: dataToMap.data,
		total: total,
	});
	const otherData = getPieChartLegend({
		items: dataToMap.otherData,
		total: total,
	});
	return {
		data: data,
		otherData: otherData,
	};
};
